import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { useReactMediaRecorder } from 'react-media-recorder';
import { EventNames } from '../../../common/constants/constants';
import { RecordPreview } from '../RecordPreview/RecordPreview';
import { BlockedDevice, CameraBlockedPanel, cameraProblemFromError } from './CameraBlockedPanel';
import { canReloadForRecorder, reloadIntoRecorder } from './recorderReload';
import { VideoRecordButton } from '../VideoRecordButton/VideoRecordButton';
import {
  PreviewWrapper,
  PreviewControlsWrapper,
  ButtonsWrapper,
  BackButton,
  GetReadyText,
  RecordText,
  VideoPreviewWrapper,
  VideoPreview,
  PlayIcon,
  SubmitControlsWrapper,
  RetakeButton,
  SubmitButton,
} from './RecordVideoComponent.styles';
import { RecordBackIcon } from '../../../common/assets/RecordBackIcon';
import { VideoToolStrings } from '../../../common/localization/en';
import { Counter } from './Counter/Counter';
import { RecordPlayIcon } from '../../../common/assets/RecordPlayIcon';
import { useTrackEvent } from '../../../common/hooks/useTrackEvent';
import SwitchCameraIcon from '../../../common/assets/playerIcons/SwitchCameraIcon';
import { useAppDispatch } from '../../../store/store';
import { setHideCrispChat } from '../../../store/slices/ui';
import { useCrispChat } from '../../../services/crisp/useCrispChat';
import {
  consumeRecorderReopen,
  markRecorderRetake,
  markRecorderRetry,
  setPendingRecordedDuration,
} from '../../../services/analytics/telemetry';

export const RECORD_TIME_LIMIT = 30;

interface RecordVideoComponentProps {
  setBlob: (val: string | undefined) => void;
  setFile: (val: File | null) => void;
  handleRemount: () => void;
  setStoryId: (val: string) => void;
  onFileChange: (file: File | null) => void;
  onRefresh: () => void;
  setIsRecordError: (error: boolean) => void;
  toggleCamera: () => void;
  camera: 'user' | 'environment';
}
const MEDIA_CREATING_TIMEOUT = 5000;

/** After a NotAllowedError, read the camera and microphone permission states
 *  (the recorder asks for both in one request, so the error alone says
 *  neither which device nor whether the browser will ask again).
 *  - kind: 'dismissed' when the browser will ask again (the refused device is
 *    still 'prompt': Chrome's close button, usually Safari's "Don't Allow"),
 *    'denied' when it will not.
 *  - device: the one not granted; 'both' when neither is, or unknown.
 *  Browsers without these Permissions API names (Firefox) give denied/both. */
type Denial = {
  kind: 'denied' | 'dismissed';
  device: BlockedDevice;
  /** Raw states (null when the browser cannot report them). */
  cam: PermissionState | null;
  mic: PermissionState | null;
};
/** A refusal faster than this came from the browser, not from a person. */
const INSTANT_REFUSAL_MS = 500;

const resolveDenial = async (): Promise<Denial> => {
  const state = async (name: string): Promise<PermissionState | null> => {
    try {
      return (await navigator.permissions.query({ name: name as PermissionName })).state;
    } catch {
      return null;
    }
  };
  const [cam, mic] = await Promise.all([state('camera'), state('microphone')]);
  if (cam === null && mic === null) return { kind: 'denied', device: 'both', cam, mic };
  const camOk = cam === 'granted';
  const micOk = mic === 'granted';
  const device: BlockedDevice = camOk && !micOk ? 'microphone' : micOk && !camOk ? 'camera' : 'both';
  const refused = device === 'microphone' ? [mic] : device === 'camera' ? [cam] : [cam, mic];
  const kind = refused.some((s) => s === 'denied') ? 'denied' : 'dismissed';
  return { kind, device, cam, mic };
};

export const RecordVideoComponent: FC<RecordVideoComponentProps> = ({
  setBlob,
  handleRemount,
  setFile,
  onFileChange,
  onRefresh,
  setIsRecordError,
  toggleCamera,
  camera,
}) => {
  // A fresh constraints object every render re-ran the library's permission
  // effect on every render: two getUserMedia calls per open when granted, and
  // a request-per-frame loop (465 calls in 7.5 s in QA) when denied.
  const videoConstraints = useMemo(() => ({ facingMode: camera }), [camera]);
  const { status, startRecording, stopRecording, mediaBlobUrl, previewStream, error } = useReactMediaRecorder({
    video: videoConstraints,
    askPermissionOnMount: true,
  });

  const { trackEvent } = useTrackEvent();

  const inputRef = useRef<HTMLInputElement | null>(null);
  const previewVideoRef = useRef<HTMLVideoElement | null>(null);

  const [isCountDownStated, setIsCountDownStated] = useState(false);
  const [isPreviewVideoPlaying, setIsPreviewVideoPlaying] = useState(false);
  const [isRecorded, setIsRecorded] = useState(false);
  const [isMediaCreatingTimeout, setIsMediaCreatingTimeout] = useState(false);
  const dispatch = useAppDispatch();
  const { hideCrispChatToggle } = useCrispChat();
  const [isRearCameraSupported, setIsRearCameraSupported] = useState(false);

  useEffect(() => {
    dispatch(setHideCrispChat(true));
    hideCrispChatToggle();
    return () => {
      dispatch(setHideCrispChat(false));
    };
  }, []);

  const checkRearCamera = () => {
    if (/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
      setIsRearCameraSupported(true);
    }
  };

  const clearInput = useCallback(() => {
    if (inputRef?.current) {
      inputRef.current.value = '';
    }
  }, []);

  useEffect(() => {
    if (isRecorded) {
      setTimeout(() => {
        setIsMediaCreatingTimeout(true);
      }, MEDIA_CREATING_TIMEOUT);
    }
  }, [isRecorded]);

  const handleRecordingError = () => {
    onRefresh();
    setIsRecordError(true);
    trackEvent({ action: EventNames.RecordVideoError });
    trackEvent({ action: EventNames.record_error, code: error || 'encoder', stage: status });
  };

  // ── Telemetry (spec v2): recorder lifecycle ──
  const openedAtRef = useRef(performance.now());
  const readyAtRef = useRef<number | null>(null);
  const recordingStartedAtRef = useRef<number | null>(null);
  const lastStopDurationRef = useRef<number | null>(null);
  const permissionReportedRef = useRef(false);
  const [denial, setDenial] = useState<Denial | null>(null);
  // Both allowed for the site, yet refused again after the recovery reload:
  // the device's own privacy settings block the browser.
  const [systemBlocked, setSystemBlocked] = useState(false);
  const entryRef = useRef<'retake' | 'retry' | 'ticket'>('ticket');
  const statusRef = useRef(status);
  statusRef.current = status;
  const countdownRef = useRef(false);
  const prevStatusRef = useRef(status);

  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = status;
    const permissionOutcome: Record<string, string> = {
      permission_denied: 'denied',
      // AbortError: access was granted but the device failed to start. Not a
      // refusal (it was wrongly mapped to 'dismissed' before).
      media_aborted: 'error',
      no_specified_media_found: 'error',
      media_in_use: 'error',
      invalid_media_constraints: 'error',
      no_constraints: 'error',
      recorder_error: 'error',
    };
    if (!permissionReportedRef.current) {
      // The library returns to 'idle' after BOTH success and failure and puts
      // the failure reason in `error` ('NONE' when fine), so read that.
      if (status === 'idle' && prev === 'acquiring_media') {
        permissionReportedRef.current = true;
        const failed = !!error && error !== 'NONE';
        const msToResult = Math.round(performance.now() - openedAtRef.current);
        const report = (outcome: string, device: BlockedDevice | null = null) => {
          trackEvent({
            action: EventNames.camera_permission_result,
            outcome,
            blocked_device: device,
            error_code: failed ? error : null,
            ms_to_result: msToResult,
            facing: camera,
          });
          if (failed) trackEvent({ action: EventNames.record_error, code: error, stage: 'permission' });
        };
        if (failed && error === 'permission_denied') {
          // Chrome raises the same NotAllowedError for "Block" and for closing
          // the prompt, and the recorder library drops the message. The
          // permission state tells them apart: still "prompt" means the
          // person dismissed it; "denied" means they blocked it.
          resolveDenial().then((d) => {
            // "Try again" after allowing both, refused at once without a
            // prompt, with nothing set to Block: the browser is still holding
            // this page's earlier refusal. Only a reload clears it.
            const browserDidNotAsk =
              entryRef.current === 'retry' &&
              msToResult < INSTANT_REFUSAL_MS &&
              d.cam !== 'denied' &&
              d.mic !== 'denied';
            if (browserDidNotAsk && canReloadForRecorder()) {
              trackEvent({
                action: EventNames.camera_permission_result,
                outcome: d.kind,
                blocked_device: d.device,
                error_code: error,
                ms_to_result: msToResult,
                facing: camera,
                next: 'reload',
              });
              reloadIntoRecorder();
              return;
            }
            const system = browserDidNotAsk && d.cam === 'granted' && d.mic === 'granted';
            if (system) setSystemBlocked(true);
            setDenial(d);
            report(system ? 'system' : d.kind, d.device);
          });
        } else {
          report(failed ? permissionOutcome[error] || 'error' : 'granted');
        }
      } else if (permissionOutcome[status]) {
        permissionReportedRef.current = true;
        trackEvent({
          action: EventNames.camera_permission_result,
          outcome: permissionOutcome[status],
          error_code: status,
          ms_to_result: Math.round(performance.now() - openedAtRef.current),
          facing: camera,
        });
      }
    } else if (permissionOutcome[status] && prev !== status) {
      trackEvent({ action: EventNames.record_error, code: status, stage: prev });
    }
    if (status === 'recording' && prev !== 'recording') {
      recordingStartedAtRef.current = performance.now();
      countdownRef.current = false;
    }
    if (status === 'stopping' && prev === 'recording') {
      const durationS = recordingStartedAtRef.current
        ? Math.round((performance.now() - recordingStartedAtRef.current) / 100) / 10
        : null;
      lastStopDurationRef.current = durationS;
      setPendingRecordedDuration(durationS);
      trackEvent({
        action: EventNames.record_stop,
        duration_s: durationS,
        reason: durationS !== null && durationS >= RECORD_TIME_LIMIT - 0.5 ? 'max_duration' : 'user',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    if (!previewStream || readyAtRef.current !== null) return;
    readyAtRef.current = performance.now();
    const track = previewStream.getVideoTracks()[0];
    const settings = track?.getSettings?.() ?? {};
    trackEvent({
      action: EventNames.recorder_ready,
      ms_since_open: Math.round(readyAtRef.current - openedAtRef.current),
      video_w: settings.width ?? null,
      video_h: settings.height ?? null,
      facing: camera,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewStream]);

  useEffect(() => {
    checkRearCamera();
  }, []);

  useEffect(() => {
    if (isMediaCreatingTimeout && status === 'stopping') {
      handleRecordingError();
    }
  }, [isMediaCreatingTimeout, status]);

  useEffect(() => {
    if (isRecorded && mediaBlobUrl && status === 'stopped') {
      setTimeout(async () => {
        const fileBlob = await fetch(mediaBlobUrl).then((res) => res.blob());
        if (fileBlob.size === 0) {
          handleRecordingError();
        }
      }, 0);
    }
  }, [isRecorded, mediaBlobUrl, status]);

  useEffect(() => {
    // to show thumbnail preview on IOS

    previewVideoRef?.current?.load();
  }, [mediaBlobUrl]);

  useEffect(() => {
    trackEvent({ action: EventNames.Record_Video_Page });
    entryRef.current = consumeRecorderReopen() ?? 'ticket';
    trackEvent({ action: EventNames.recorder_open, entry: entryRef.current });
    // A remount mid-countdown or mid-recording (permission churn, camera
    // switch) silently discards the take: report it so every record_start
    // has a matching stop or error.
    return () => {
      if (statusRef.current === 'recording' || countdownRef.current) {
        trackEvent({
          action: EventNames.record_error,
          code: 'unmounted',
          stage: statusRef.current === 'recording' ? 'recording' : 'countdown',
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangeUploadVideoHandler = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0] || null;

      if (file) {
        trackEvent({ action: EventNames.Record_Video_Page_File_Selected, size: file.size });
        trackEvent({ action: EventNames.record_page_choose_video_click, file_size: file.size });
        clearInput();
        setFile(file);
        onFileChange(file);
        return;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clearInput, setFile],
  );

  const onBackClick = () => {
    trackEvent({ action: EventNames.Back_Button_Click });
    trackEvent({
      action: EventNames.record_back,
      stage: mediaBlobUrl ? 'preview' : status === 'recording' || isCountDownStated ? 'recording' : 'before_rec',
      duration_recorded_s: recordingStartedAtRef.current
        ? Math.round((performance.now() - recordingStartedAtRef.current) / 100) / 10
        : null,
    });
    handleRemount();
  };

  const onStartRecording = () => {
    trackEvent({ action: EventNames.Record_Button_Click });
    trackEvent({
      action: EventNames.record_start,
      ms_since_ready: readyAtRef.current !== null ? Math.round(performance.now() - readyAtRef.current) : null,
    });
    countdownRef.current = true;

    setIsCountDownStated(true);
  };

  const handlePlay = () => {
    const video = previewVideoRef.current;

    if (video?.paused) {
      video.play();
    } else {
      video?.pause();
    }
  };

  const isRecording = status === 'recording';

  const recordingControls = useMemo(() => {
    return (
      <PreviewControlsWrapper>
        {isCountDownStated || isRecording ? (
          <RecordText>{isRecording ? VideoToolStrings.Recording : VideoToolStrings.GetReady}</RecordText>
        ) : (
          <GetReadyText>
            PRESS{' '}
            <Typography display="inline" color="#F00">
              REC
            </Typography>{' '}
            WHEN READY
          </GetReadyText>
        )}
        <ButtonsWrapper>
          <BackButton display="flex">
            <IconButton color="inherit" onClick={onBackClick} aria-label="back" size="large" sx={{ fontSize: '36px' }}>
              <RecordBackIcon />
            </IconButton>
          </BackButton>
          <Box>
            <VideoRecordButton
              handleStart={onStartRecording}
              handleStop={() => {
                stopRecording();
                setIsRecorded(true);
              }}
              isRecording={isRecording}
            />
          </Box>
          {isRearCameraSupported && !isRecording && (
            <Box
              onClick={() => {
                toggleCamera();
                onRefresh();
              }}
              style={{ display: 'flex', justifyContent: 'right', marginRight: '39px' }}
            >
              <SwitchCameraIcon />
            </Box>
          )}
        </ButtonsWrapper>
      </PreviewControlsWrapper>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startRecording, stopRecording, status, mediaBlobUrl, handleRemount, onChangeUploadVideoHandler]);

  // Camera could not start (blocked, dismissed, busy, missing): show guidance
  // instead of a black screen. Only when there is no live camera at all — the
  // library reuses `error` for recording failures, which keep their own flow.
  const rawProblem = !mediaBlobUrl && !previewStream ? cameraProblemFromError(error) : null;
  // A refusal waits (a few ms) for the permission states, so the panel names
  // the right device and icon from its first frame.
  const cameraProblem =
    rawProblem === 'blocked' && !denial ? null : rawProblem === 'blocked' && systemBlocked ? 'system' : rawProblem;

  const retry = useCallback(() => {
    markRecorderRetry();
    onRefresh();
  }, [onRefresh]);

  // Allowing camera and microphone from the address bar while the panel is
  // up retries by itself (Chrome and Safari report the change); no need to
  // find the button.
  useEffect(() => {
    if (cameraProblem !== 'blocked' || !navigator.permissions?.query) return undefined;
    let done = false;
    const statuses: PermissionStatus[] = [];
    const onChange = () => {
      if (done || statuses.some((st) => st.state !== 'granted')) return;
      done = true;
      trackEvent({
        action: EventNames.camera_blocked_action,
        problem: 'blocked',
        device: denial?.device ?? null,
        choice: 'auto_retry',
      });
      // Outside a React event React 17 does not batch: the refresh's three
      // state updates would mount the recorder twice (two camera requests).
      unstable_batchedUpdates(retry);
    };
    Promise.all(['camera', 'microphone'].map((name) => navigator.permissions.query({ name: name as PermissionName })))
      .then((list) => {
        if (done) return;
        statuses.push(...list);
        list.forEach((st) => st.addEventListener('change', onChange));
      })
      .catch(() => undefined);
    return () => {
      done = true;
      statuses.forEach((st) => st.removeEventListener('change', onChange));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraProblem]);

  return (
    <PreviewWrapper>
      {cameraProblem && (
        <CameraBlockedPanel
          problem={cameraProblem}
          device={denial?.device}
          onRetry={retry}
          onBack={() => {
            trackEvent({ action: EventNames.Back_Button_Click });
            handleRemount();
          }}
        />
      )}
      {!mediaBlobUrl && (
        <>
          <Counter
            initialValue={3}
            setIsCountStarted={setIsCountDownStated}
            startRecording={startRecording}
            isCountDownStarted={isCountDownStated}
            isRecording={isRecording}
          />
          <RecordPreview stream={previewStream} />
          {recordingControls}
        </>
      )}
      {mediaBlobUrl && (
        <VideoPreviewWrapper>
          <VideoPreview
            as="video"
            src={mediaBlobUrl}
            ref={previewVideoRef}
            onClick={handlePlay}
            onPlay={() => {
              setIsPreviewVideoPlaying(true);
              trackEvent({ action: EventNames.record_preview_play });
            }}
            onPause={() => {
              setIsPreviewVideoPlaying(false);
            }}
            onEnded={() => {
              setIsPreviewVideoPlaying(false);
            }}
            autoPlay={false}
            controls={false}
            playsInline={true}
            loop={true}
            preload="auto"
          />
          {!isPreviewVideoPlaying && (
            <PlayIcon onClick={handlePlay}>
              <RecordPlayIcon />
            </PlayIcon>
          )}
          <PreviewControlsWrapper>
            <SubmitControlsWrapper>
              <RetakeButton
                variant="text"
                onClick={() => {
                  trackEvent({ action: EventNames.record_retake, previous_duration_s: lastStopDurationRef.current });
                  markRecorderRetake();
                  onRefresh();
                }}
              >
                {VideoToolStrings.Retake}
              </RetakeButton>
              {isRecorded && status !== 'stopping' ? (
                <SubmitButton
                  variant="text"
                  onClick={() => {
                    setBlob(mediaBlobUrl);
                  }}
                >
                  Submit Clip
                </SubmitButton>
              ) : (
                <CircularProgress
                  sx={{
                    justifySelf: 'center',
                    color: '#ED2E38',
                  }}
                />
              )}
            </SubmitControlsWrapper>
          </PreviewControlsWrapper>
        </VideoPreviewWrapper>
      )}
    </PreviewWrapper>
  );
};
